import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom'
import { scaleLinear } from 'd3-scale'
import { line, area } from 'd3-shape'
import { easePolyInOut } from 'd3-ease'
import rk4 from 'ode-rk4'
import { range } from 'd3-array'
import { format } from 'd3-format'

function copy(x) {
  return Object.assign({},x)
}

const thousands = format("~s")
const withComma = format(",")

const simulate = (f,t0,y0,step,tmax) => {
  let integrator = rk4(y0, f, t0, step)
  let t = t0
  const y = y0
  const ta = []
  const ya = []
  ta.push(t0)
  ya.push(copy(y))

  let peakInfected = 0
  let peakIndex = 0

  while(true){
    t = t+step
    if(t>tmax) break
    integrator=integrator.step()
    ya.push(copy(integrator.y))
    ta.push(t)

    if (integrator.y[1] > peakInfected) { 
      peakInfected = integrator.y[1];
      peakIndex = ta.length;
    }
  }
  return {
    t:ta,
    y:ya,
    peakInfected,
    peakIndex
  };
}

const scrollScale = scaleLinear()
  .domain([0, 400])
  .clamp(true)

const initialStartMeasure = 104
const scrollToMeasure = (pixel) => {
  return initialStartMeasure - easePolyInOut(scrollScale(pixel)) * 50
}

const SIRmodelVis = ({ portalId }) => {
  const [b1, setB1] = useState(0.12)
  const [b2, setB2] = useState(0.06)
  const [gamma, setGamma] = useState(0.05)
  const [startMeasures, setStartMeasures] = useState(initialStartMeasure)
  const svgRef = useRef()
  const [simulationState, setSimulationState] = useState(null)
  const [simPeak, setSimPeak] = useState(null)
  const [worseState, setWorseState] = useState(null)
  const [worsePeak, setWorsePeak] = useState(null)

  const [mouseDown, setMouseDown] = useState(false)
  const [initialInteraction, setInitialInteraction] = useState(false)

  const I0 = 0.001
  const step = 1
  const tmax = 400
  const populationSum = 100000

  const animate = useRef()
  const requestRef = useRef()
  React.useEffect(() => {
    animate.current = () => {
      const svgRect = svgRef.current.getBoundingClientRect()
      const progressPixels = Math.max(0, -(svgRect.top - window.innerHeight + 150))
      const newMeasureTime = Math.round(scrollToMeasure(progressPixels))
      
      if (!initialInteraction) {
        setStartMeasures(newMeasureTime)
        requestRef.current = requestAnimationFrame(animate.current);
      }
    };
  }, [initialInteraction]);

  React.useEffect(() => { animate.current() }, [])

  useEffect(() => {
    const sir = (dydt, y, t) => {
      const beta = (t < startMeasures) ? b1 : b2
  
      dydt[0] = -beta * y[0] * y[1];
      dydt[1] = beta * y[0] * y[1] - gamma * y[1];
      dydt[2] = gamma *y[1];
    }

    const sir_sol = simulate(sir,0,[1.0-I0,I0,0.0],step,tmax)

    //const peakIndex = maxIndex(sir_sol.y, d => d[1])
    setSimPeak({
      index: sir_sol.peakIndex,
      value: sir_sol.peakInfected
    })

    setSimulationState(sir_sol.y)
  }, [startMeasures, b1, b2])

  useEffect(() => {
    const noMeasuresSir = (dydt, y, t) => {
      const beta = b1
  
      dydt[0] = -beta * y[0] * y[1];
      dydt[1] = beta * y[0] * y[1] - gamma * y[1];
      dydt[2] = gamma *y[1];
    }

    const sir_sol = simulate(noMeasuresSir,0,[1.0-I0,I0,0.0],step,tmax)
    
    // const worstIndex = maxIndex(sir_sol.y, d => d[1])
    setWorsePeak({
      index: sir_sol.peakIndex,
      value: sir_sol.peakInfected
    })

    setWorseState(sir_sol.y)
  }, [b1])
  

  const heightScale = scaleLinear()
  .domain([0, 1])
  .range([300, 0])

  const width = Math.min(window.innerWidth, 600)

  const widthScale = scaleLinear()
    .domain([0, tmax / step])
    .range([25, width - 25])

  const sLine = line()
    .x((d, i) => widthScale(i))
    .y(d => heightScale(d[1] + d[0]))

  const sArea = area()
    .x((d, i) => widthScale(i))
    .y1(d => heightScale(d[1] + d[0]))
    .y0(d => heightScale(d[1]))

  const iLine = line()
    .x((d, i) => widthScale(i))
    .y(d => heightScale(d[1]))

  const iArea = area()
    .x((d, i) => widthScale(i))
    .y1(d => heightScale(d[1]))
    .y0(heightScale(0));

  const rLine = line()
    .x((d, i) => widthScale(i))
    .y(d => heightScale(d[1] + d[0]))

  const rArea = area()
    .x((d, i) => widthScale(i))
    .y1(heightScale(1))
    .y0(d => heightScale(d[1] + d[0]));

  const triggerInteraction = (e) => {
    e.preventDefault()

    const mx = (e.touches) ? e.touches[0].clientX : e.clientX
    const rect = svgRef.current.getBoundingClientRect()
    const newStartMeasures = widthScale.invert(mx - rect.left) * step
    setStartMeasures(newStartMeasures)

    setInitialInteraction(true)
    setMouseDown(true)
  }

  const stopInteraction = () => {
    setMouseDown(false)
  }

  const drag = (e) => {
    e.preventDefault()

    if (mouseDown) {
      const mx = (e.touches) ? e.touches[0].clientX : e.clientX
      const rect = svgRef.current.getBoundingClientRect()
      const newStartMeasures = widthScale.invert(mx - rect.left) * step
      setStartMeasures(newStartMeasures)
    }
  }

  // Annotations
  const measureLineX = widthScale(startMeasures / step)

  let peakReduction = null
  let peakDelay = null
  if (simPeak && worsePeak) {
    peakReduction = (worsePeak.value - simPeak.value) / worsePeak.value

    peakDelay = (simPeak.index - worsePeak.index) * step
  }
  const reductionX = width - 120 // 480

  const yAxisTickCount = 20

  // Portal Out
  let twoWeeksCount, fourWeeksCount
  if (worseState) {
    twoWeeksCount = Math.round(worseState[Math.round(14 / 0.2)][1] * 100000)
    fourWeeksCount = Math.round(worseState[Math.round(28 / 0.2)][1] * 100000)
  }
  
  return ReactDOM.createPortal(
    <React.Fragment>
      <div className="sir-legend" style={{ userSelect: "none" }}>
        <strong>Infected Population:</strong>
        <ul>
          <li>
            <span className="sir-legend-item">
              <svg className="sir-inline-legend" width="12" height="12">
                <g transform="translate(4,11)">
                  <polyline 
                    points="0,0 0,-10 6,-7 0,-4"
                    stroke="red"
                    fill="none"
                  />
                </g>
                <line x2="12" y1="11" y2="11" stroke="red" strokeDasharray="2,1" />
              </svg> Given No Social Distancing {(worsePeak) ? 
                <span>– Peak Infections: <strong>{withComma(Math.round(worsePeak.value * 100000))}</strong></span> 
                : null }
            </span>
          </li>
          <li>
            <span className="sir-legend-item">
              <svg className="sir-inline-legend" width="12" height="14">
                <g transform="translate(4,11)">
                  <polyline 
                    points="0,0 0,-10 6,-7 0,-4"
                    stroke="red"
                    fill="red"
                  />
                </g>
                <line x2="12" y1="11" y2="11" stroke="red" strokeWidth="2" />
              </svg> With Social Distancing starting Day {Math.round(startMeasures)} 
              {(simPeak) ? <span> – Peak Infections: <strong>{withComma(Math.round(simPeak.value * 100000))}</strong></span> : null}
            </span>
          </li>
        </ul>
      </div>
      <svg width="100%" height="400"
        className="SIR-svg"
        ref={svgRef}
        onTouchStart={triggerInteraction}
        onMouseDown={triggerInteraction}
        onTouchEnd={stopInteraction}
        onMouseUp={stopInteraction}
        onMouseMove={drag}
        onTouchMove={drag}
      >
        <g transform="translate(0, 50)">
          <g className="sir-x-axis" transform="translate(0,300)">
             <line x1="0" x2="600" stroke="black" />
            {range(Math.floor(tmax / 7)).map((i) => {
              const x = widthScale(i * 7 / step)

              return <g key={i} transform={`translate(${x},0)`}>
                <line y2={(i % 6 === 0) ? 7 : 4} stroke="black" />
                {(i % 6 === 0) ?
                  <text y="16" style={{ fontSize: "10px", fill: "#888" }} textAnchor="middle">Week {i}</text> 
                : null}
              </g>
            })}
          </g>

          <g className="sir-y-axis">
            {range(yAxisTickCount).map((i) => {
              const j = i + 1
              return <g key={i} transform={`translate(0,${heightScale(j / 20)})`}>
                <line x1={(j % 5 === 0) ? 11 : 17} x2={22} stroke="#222" />
                {(j % 5 === 0) ?
                  <text y="-2" x="22" textAnchor="end" style={{ fontSize: "10px", fill: "#222" }}>{thousands(j * populationSum / yAxisTickCount)}</text>
                : null}
              </g>
            })}
          </g>

          {(simulationState) ? 
          <React.Fragment>
            <path d={sArea(simulationState)} fill="#aaa" />
            <path d={iArea(simulationState)} stroke="none" fill="#c60" />
            <path d={rArea(simulationState)} fill="#979" />
            <path d={iLine(worseState)} stroke="red" strokeWidth="2" strokeDasharray="2,1" fill="none" />

            <path id="text-path-line" d={sLine(simulationState)} fill="none" stroke="none" />

            <g transform="translate(0, 11)">
              <text style={{ fontSize: "10px", fill: "#555" }}>
                <textPath href="#text-path-line" startOffset="3">
                  Susceptible Population
                </textPath>
              </text>
            </g>

            <text style={{ fontSize: "10px", fill: "purple" }} textAnchor="end" x={widthScale(simulationState.length - 1) - 3} y="11">
              Recovered Population
            </text>

          </React.Fragment>: 
            null
          }

          {(worsePeak) ?
          <g transform={`translate(${widthScale(worsePeak.index)}, ${heightScale(worsePeak.value)})`}>
            <polyline 
              points="0,0 0,-10 6,-7 0,-4"
              stroke="red"
              fill="none"
            />
          </g>
          : null}

          {(simPeak) ?
          <g transform={`translate(${widthScale(simPeak.index)}, ${heightScale(simPeak.value)})`}>
            <polyline 
              points="0,0 0,-10 6,-7 0,-4"
              stroke="red"
              fill="red"
            />
          </g>
          : null}

          {(peakReduction) ?
          <React.Fragment>
            <g>
              <line x1={reductionX - 30} x2={reductionX} y1={heightScale(worsePeak.value)} y2={heightScale(worsePeak.value)} stroke="white" strokeDasharray={"1,2,1,2,80"} />
              <line x1={widthScale(worsePeak.index)} x2={reductionX - 30} y1={heightScale(worsePeak.value)} y2={heightScale(worsePeak.value)} stroke="white" strokeDasharray={"1,4"} />

              <line x1={reductionX - 30} x2={reductionX} y1={heightScale(simPeak.value)} y2={heightScale(simPeak.value)} stroke="white" strokeDasharray={"1,2,1,2,80"} />
              <line x1={widthScale(simPeak.index)} x2={reductionX - 30} y1={heightScale(simPeak.value)} y2={heightScale(simPeak.value)} stroke="white" strokeDasharray={"1,4"} />

              <g transform={`translate(${reductionX},${(heightScale(worsePeak.value) + heightScale(simPeak.value)) / 2})`}>
                <text x="2" y="-5" style={{fontSize: "12px", fill: "#fff", fontWeight: "bold" }}>Peak Load</text>
                <text x="2" y="12" fill="#fff" fontWeight="bold">-{Math.round(peakReduction * 100)}%</text>
              </g>

              {(peakReduction > 0.06) ?
              <g transform={`translate(${reductionX - 12}, 0)`}>
                <polyline
                  points={`-3, ${heightScale(worsePeak.value) + 1}
                    3, ${heightScale(worsePeak.value) + 1}
                    3, ${heightScale(simPeak.value) + -9}
                    6, ${heightScale(simPeak.value) + -9}
                    0, ${heightScale(simPeak.value) + -1}
                    -6, ${heightScale(simPeak.value) + -9}
                    -3, ${heightScale(simPeak.value) + -9}
                  `}
                  fill="white"
                />
              </g> : null}
            </g>
            {(peakDelay > 0) ?
            <g>
              <g transform={`translate(${widthScale(simPeak.index)},${heightScale(worsePeak.value) - 30})`}>
                <text x="2" y="-20" style={{fontSize: "12px", fill: "#fff", fontWeight: "bold" }}>Peak Delay</text>
                <text x="2" y="-4" fill="#fff" fontWeight="bold">~{Math.round(peakDelay)} days</text>
              </g>
              {(peakDelay > 2) ?
              <g transform={`translate(0,${heightScale(worsePeak.value)-40})`}>
                <polyline 
                  points={`${widthScale(worsePeak.index)},-3
                    ${widthScale(worsePeak.index)},3
                    ${widthScale(simPeak.index) - 9},3
                    ${widthScale(simPeak.index) - 9},6
                    ${widthScale(simPeak.index) - 1},0
                    ${widthScale(simPeak.index) - 9},-6
                    ${widthScale(simPeak.index) - 9},-3
                  `}
                  fill="white"
                />
              </g> : null}
            </g> : null}
          </React.Fragment>
          : null}
        </g>
        <g transform={`translate(${measureLineX},0)`}>
          <line
            y1={0}
            y2={350}
            strokeWidth="1.5"
            stroke="black"
          />
          <text y="10" x="3" style={{fontSize: "12px", fill: "#111", fontWeight: "bold" }}>Drag Me {(worsePeak && startMeasures / step > worsePeak.index) ? "...maybe to the left" : null}</text>
          <text y="24" x="3" style={{fontSize: "12px", fill: "#888" }}>Social Distancing begins </text>
              <text y="38" x="3" style={{fontSize: "12px", fill: "#888" }}>Day {Math.round(startMeasures)} after {populationSum * I0}th case</text>
        </g>
        <g transform={`translate(${width},385)`}>
          <text style={{ fontSize: "10px", fill: "#444" }} textAnchor="end">
            Toy simulation.
            β<tspan baselineShift="sub" fontSize="7px">1</tspan> = {b1},
            β<tspan baselineShift="sub" fontSize="7px">2</tspan> = {b2},
            and γ = {gamma}
          </text>
        </g>
      </svg>
    </React.Fragment>, 
    document.getElementById(portalId)
  );
};

export default SIRmodelVis;